import baseConfig, { type ThemeData } from '@/../config/themes';
import schemes from '@uikit/ui-syntax/dist/js/colors';
import prepareThemeColors from '@uikit/ui-kit/colors/js/utils/theme';
import { UIKIT_SCHEME_MAP } from '@/common/constants/themes.constants';
import virtuozzoColorScheme from '@/hci/config/virtuozzoColorScheme';

import acronisLogo from '@/assets/images/acronis/logo-navbar-cf.svg';
import acronisLoginLogo from '@/assets/images/acronis/logo-sign-in-cf.svg';
import acronisAboutLogo from '@/assets/images/acronis/about-icon.svg';
import acronisS3Bg from '@/assets/images/acronis/s3-cloud.svg';

import virtuozzoLogo from '@/assets/images/virtuozzo/logo-menu.svg';
import virtuozzoLoginLogo from '@/assets/images/virtuozzo/logo.svg';
import virtuozzoAboutLogo from '@/assets/images/virtuozzo/about-icon.svg';
import virtuozzoS3Bg from '@/assets/images/virtuozzo/s3-cloud.svg';

export type HciThemeData = ThemeData & {
  logo: string;
  loginLogo: string;
  logoIcon: string;
  aboutLogo: string;
  metadataDisksLink: string;
  docsLink: string;
  storageDisksLink: string;
  journalDisksLink: string;
  supportLink: string;
  contactUsLink: string;
  isoLink: string;
  termsLicenses: string;
  eulaLink: string;
  productPage: string;
  getKBLink: false | ((issue: string) => string);
  howToEnableFreezer: string;
  redundancyModesLink: string;
  storagePoliciesLink: string;
  migrationDocsLink: string;
  computeApiAccessLink: string;
  deploymentAndConfigurationLink: string;
  cssVariables: Record<string, string>;
  s3Bg: string;
  showAcronisCloudRegistrationBanner?: boolean;
  showConvertToCyberFrameBanner?: boolean;
  showRegisterInCyberFrameBanner?: boolean;
  showClusterRegistrationMenu?: boolean;
  computeIpManagementDefault: boolean;
  computeVmPublicOnPrivateNetwork: boolean;
  showVStorageName?: boolean;
}

const themes: Record<typeof APP_THEME, HciThemeData> = {
  acronis: {
    ...baseConfig.acronis,
    logo: acronisLogo,
    loginLogo: acronisLoginLogo,
    logoIcon: 'i-a-logo--32',
    aboutLogo: acronisAboutLogo,
    metadataDisksLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#deployment-and-configuration.html',
    docsLink: 'https://www.acronis.com/support/documentation/',
    storageDisksLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#deployment-and-configuration.html',
    journalDisksLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#deployment-and-configuration.html',
    supportLink: 'https://www.acronis.com/support',
    contactUsLink: 'https://www.acronis.com/support/contact-us.html',
    isoLink: 'https://download.acronis.com/vstorage/releases/7.3/x86_64/iso/acronis-cyber-frame.iso',
    termsLicenses: 'https://www.acronis.com/en-us/legal.html',
    eulaLink: 'https://www.acronis.com/en-us/support/eula.html',
    productPage: 'https://www.acronis.com/en-us',
    getKBLink: issue => `https://kb.acronis.com/search?ap=9772&s=${encodeURIComponent(issue)}`,
    howToEnableFreezer: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#provisioning-the-backup-service.html',
    redundancyModesLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#redundancy-modes.html',
    storagePoliciesLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#managing-storage-policies.html',
    migrationDocsLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#managing-migrations-from-external-platforms.html',
    computeApiAccessLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/index.html#configuring-compute-api-access.html',
    deploymentAndConfigurationLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#deployment-and-configuration.html',
    cssVariables: prepareThemeColors(UIKIT_SCHEME_MAP.DEFAULT, schemes.DefaultColorScheme),
    s3Bg: acronisS3Bg,
    showRegisterInCyberFrameBanner: true,
    showClusterRegistrationMenu: true,
    computeIpManagementDefault: false,
    computeVmPublicOnPrivateNetwork: true,
    showVStorageName: false
  },
  virtuozzo: {
    ...baseConfig.virtuozzo,
    logo: virtuozzoLogo,
    loginLogo: virtuozzoLoginLogo,
    logoIcon: 'i-v-logo--32',
    aboutLogo: virtuozzoAboutLogo,
    metadataDisksLink: 'https://www.virtuozzo.com/infrastructure-docs/admin-guide-infrastructure/',
    docsLink: 'https://www.virtuozzo.com/infrastructure-docs/',
    storageDisksLink: 'https://www.virtuozzo.com/infrastructure-docs/admin-guide-infrastructure/',
    journalDisksLink: 'https://www.virtuozzo.com/infrastructure-docs/admin-guide-infrastructure/',
    contactUsLink: 'https://support.virtuozzo.com/',
    isoLink: 'https://repo.virtuozzo.com/vz-platform/releases/8.0/x86_64/iso/vz-platform.iso',
    supportLink: 'https://www.virtuozzo.com/support/all-products/virtuozzo-hybrid-infrastructure.html',
    termsLicenses: 'https://www.virtuozzo.com/legal.html',
    eulaLink: 'https://www.virtuozzo.com/legal.html',
    productPage: 'https://www.virtuozzo.com/products/virtuozzo-hybrid-infrastructure.html',
    getKBLink: false,
    howToEnableFreezer: 'https://www.virtuozzo.com/infrastructure-docs/admin-guide-provisioning-backup-service/',
    redundancyModesLink: 'https://www.virtuozzo.com/infrastructure-docs/getting-started-redundancy-modes/',
    storagePoliciesLink: 'https://www.virtuozzo.com/infrastructure-docs/admin-guide-managing-storage-policies/',
    migrationDocsLink: 'https://www.virtuozzo.com/infrastructure-docs/admin-guide-migrations-from-external-platforms/',
    computeApiAccessLink: 'https://www.virtuozzo.com/infrastructure-docs/admin-guide-compute-api-access/',
    deploymentAndConfigurationLink: 'https://www.virtuozzo.com/infrastructure-docs/admin-guide-infrastructure/',
    cssVariables: prepareThemeColors(UIKIT_SCHEME_MAP.VIRTUOZZO, virtuozzoColorScheme),
    s3Bg: virtuozzoS3Bg,
    showConvertToCyberFrameBanner: true,
    showClusterRegistrationMenu: false,
    computeIpManagementDefault: true,
    computeVmPublicOnPrivateNetwork: false,
    showVStorageName: true
  },
  'vz-converted': {
    ...baseConfig['vz-converted'],
    logo: acronisLogo,
    loginLogo: acronisLoginLogo,
    logoIcon: 'i-a-logo--32',
    aboutLogo: acronisAboutLogo,
    metadataDisksLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#deployment-and-configuration.html',
    docsLink: 'https://www.acronis.com/support/documentation/',
    storageDisksLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#deployment-and-configuration.html',
    journalDisksLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#deployment-and-configuration.html',
    supportLink: 'https://www.acronis.com/support',
    contactUsLink: 'https://www.acronis.com/support/contact-us.html',
    isoLink: 'https://download.acronis.com/vstorage/releases/8.0/x86_64/iso/acronis-cyber-frame.iso',
    termsLicenses: 'https://www.acronis.com/en-us/legal.html',
    eulaLink: 'https://www.acronis.com/en-us/support/eula.html',
    productPage: 'https://www.acronis.com/en-us',
    getKBLink: issue => `https://kb.acronis.com/search?ap=9772&s=${encodeURIComponent(issue)}`,
    howToEnableFreezer: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#provisioning-the-backup-service.html',
    redundancyModesLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#redundancy-modes.html',
    storagePoliciesLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#managing-storage-policies.html',
    migrationDocsLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#managing-migrations-from-external-platforms.html',
    computeApiAccessLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/index.html#configuring-compute-api-access.html',
    deploymentAndConfigurationLink: 'https://dl.acronis.com/u/software-defined/html/AcronisCyberFrame_8_0_admins_guide_en-US/#deployment-and-configuration.html',
    cssVariables: prepareThemeColors(UIKIT_SCHEME_MAP.DEFAULT, schemes.DefaultColorScheme),
    s3Bg: acronisS3Bg,
    showRegisterInCyberFrameBanner: true,
    showClusterRegistrationMenu: true,
    computeIpManagementDefault: false,
    computeVmPublicOnPrivateNetwork: true,
    showVStorageName: false
  }
};

export default themes;
